/* ---------------------------------------------------------------------------
   Ready Cart BD — the order form.

   Three fields, one POST, one thank-you panel. This is the last screen before a
   cash-on-delivery order and the only place on the site where a saved tap is
   worth this much, so almost every decision below is about not losing someone
   who has already decided.

   ---------------------------------------------------------------------------
   ⚠ THIS FILE IS NOT AN ENHANCEMENT. Everything else on the cart page works
   with JavaScript blocked — the steppers are links, the suggestion rows are
   real forms — and `rcb-cart.js`'s header says so proudly. This one is
   different: an order is a cross-origin POST to a Worker, so with the script
   blocked there is no order, and the form says so in a `<noscript>` and gives
   the phone number.

   That is not a regression from what it replaced. It was checked against the
   live storefront on 2026-08-22: the checkout's `availablePaymentLines` offers
   only `AnyStripeSharedTokenPaymentMethod`, so Shopify's own checkout has no
   cash-on-delivery method and a COD customer could never have completed it. The
   fallback button that used to sit here could not finish an order either.

   ---------------------------------------------------------------------------
   THE PIXEL CONTRACT — the same one at the top of `assets/rcb-product.js`,
   extended by one event. Nothing here talks to Meta or TikTok directly.

     rcb:add        the buy box                       (rcb-product.js)
     rcb:attach     an accessory taken alongside      (rcb-product.js, rcb-cart.js)
     rcb:checkout   FIRST interaction with this form  ← new

   `snippets/rcb-meta.liquid` and `snippets/rcb-tiktok.liquid` listen. Neither
   knows this file exists, and this file knows neither of theirs — which is what
   let the TikTok pixel be installed later without touching a line of the order
   path, and is why a third platform would cost one snippet and nothing here.

   ---------------------------------------------------------------------------
   ⚠ THERE IS NO `rcb:purchase`, AND PLACING AN ORDER FIRES NO CONVERSION EVENT.

   It did, until 2026-08-23. Submitting this form reported a sale to Meta and
   TikTok immediately — including for junk numbers, double taps, and orders whose
   items or address turned out to be wrong when the shop phoned the customer.

   `Purchase` now fires once, from the Worker, when the shop confirms the order
   on that call — the first moment the data is actually true. See
   `order/confirm.mjs`. The customer is long gone from this page by then, which
   is why the browser has no half of it any more and why the event's `event_id`
   no longer has to be shared with anything.

   ⚠ DO NOT ADD A CONVERSION EVENT BACK TO THIS FILE. An event here reports a
   form submission, and a form submission is not a sale. `rcb:checkout` below is
   the correct browser-side signal and is deliberately the last one.

   ⚠ The thank-you panel is UNAFFECTED. It is the customer's confirmation that
   their order exists, which it does — it was never a tracking surface.

   ---------------------------------------------------------------------------
   ⚠ INITIATECHECKOUT IS BACK, FROM HERE. It died on 2026-08-19 when the old
   COD form went embedded — its bundle dispatched the event from `popupListner`
   and nowhere else, so a form with no popup to open never fired it. There is no
   popup here either. Focus or input in a customer field starts checkout;
   submitting is the fallback for buyers accepting saved or autofilled details.
   Once per page, with a nonempty cart. Restoring details and viewing the cart
   alone do not start checkout.
   --------------------------------------------------------------------------- */

(() => {
  const root = document.querySelector('[data-rcb-order]');
  const form = root && root.querySelector('[data-rcb-order-form]');
  const rules = window.RCB_VALIDATE;
  if (!root || !form || !rules) return;

  const cartRoot = document.querySelector('[data-rcb-cart]');
  const endpoint = root.dataset.rcbEndpoint;
  const submit = form.querySelector('[data-rcb-order-submit]');
  const orderLabel = form.querySelector('[data-rcb-order-label]');
  const failLine = form.querySelector('[data-rcb-order-fail]');
  const donePanel = document.querySelector('[data-rcb-order-done]');

  /* ⚠ Queried once and held. `.rcbk-help` sits outside all three regions
     `rcb-cart.js` swaps by innerHTML (`.rcbk-items`, `.rcbk-attach`,
     `.rcbk-sum`), so the node survives every quantity change on the page. */
  const helpCard = document.querySelector('[data-rcb-help]');

  /* The cart as `/cart.js` last answered it, held from submit time. ⚠ The FULL
     lines, though only `{ id, quantity }` is ever posted: the extra fields —
     `product_title`, `url` — never go over the wire to the Worker, which prices
     every line from the variant regardless. They exist so that a failed order
     can be written out into a chat message that names what the customer
     wanted. See `orderMessage`. */
  let heldLines = [];

  const FIELDS = ['name', 'phone', 'address'];
  const cell = (name) => form.querySelector(`[data-rcb-field="${name}"]`);
  const inputOf = (name) => cell(name) && cell(name).querySelector('[data-rcb-input]');

  /* -------------------------------------------------------------------------
     Labels and placeholders come from the GENERATED rules, not from the markup.

     They are half of a validation rule rather than decoration — `আপনার নাম
     (ইংরেজিতে)` is there because Meta strips everything outside a–z before
     hashing `fn`, and the address placeholder names জেলা because nothing else
     on the form does any more. Writing them in Liquid would be a second copy
     that could drift from the rule it explains. See `order/validate.mjs`.
     ---------------------------------------------------------------------- */
  FIELDS.forEach((name) => {
    const copy = rules.COPY[name];
    const label = cell(name) && cell(name).querySelector('[data-rcb-label]');
    const input = inputOf(name);
    if (label) label.textContent = copy.label;
    if (input) input.setAttribute('placeholder', copy.placeholder);
  });

  /* -------------------------------------------------------------------------
     WHAT THE BUYER TYPED, REMEMBERED ON THE BUYER'S OWN DEVICE.

     Carried over from the app this replaced, and it was worth carrying: this
     is three fields on a Bangla phone keyboard at the last step of an order,
     and the common case is not a repeat customer, it is a form abandoned
     halfway and reopened an hour later.

     ⚠ VALIDATED ON THE WAY BACK IN, NEVER ON THE WAY OUT. A half-typed number
     is worth storing — they may come back to finish it — and worthless to
     prefill. It is checked with the same `RCB_VALIDATE` the Worker uses, so a
     stored value that would be rejected on submit is never presented as
     already-answered.

     ⚠ IT STORES A NAME, A NUMBER AND AN ADDRESS, AND NOTHING ELSE. No order, no
     cart, no identifier. The same data the browser's own autofill would keep,
     at the same origin. Keep it that way if this is ever extended.

     ⚠ AND SINCE 2026-08-28 IT IS ALSO WHAT THE PIXELS KNOW. `snippets/rcb-identity.liquid`
     reads this record in the head of EVERY page and publishes it as
     `window.RCB_ID`, which both pixel snippets pass to `ttq.identify()` and
     `fbq('init')`. A device that has typed a valid number into this form once
     is a phone number to Meta and TikTok on every page it opens afterwards —
     which is the whole answer to the 0% phone coverage their panels reported.

     ⚠ SO `ph` IS WRITTEN HERE AND NOWHERE ELSE. It is the E.164 string
     `checkPhone` returns, stored beside the raw `phone` the customer typed,
     because the head snippet runs long before `RCB_VALIDATE` is loaded and must
     never grow a phone regex of its own. There is ONE phone rule on this site —
     `order/validate.mjs` — and this is the one place its output is persisted.
     ---------------------------------------------------------------------- */
  const BUYER_KEY = 'rcb-buyer';
  const BUYER_MAX_AGE = 180 * 24 * 60 * 60 * 1000;

  const readBuyer = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(BUYER_KEY) || 'null');
      if (parsed && parsed.v === 1 && parsed.t && Date.now() - parsed.t < BUYER_MAX_AGE) return parsed;
    } catch (e) { /* private mode, full quota, or someone else's key */ }
    return null;
  };

  const saved = readBuyer();
  if (saved) {
    FIELDS.forEach((name) => {
      const input = inputOf(name);
      /* ⚠ Defers to anything the browser's own autofill has already put in. */
      if (!input || input.value) return;
      const check = name === 'phone' ? rules.checkPhone : name === 'name' ? rules.checkName : rules.checkAddress;
      const value = typeof saved[name] === 'string' ? saved[name] : '';
      if (value && check(value).ok) input.value = value;
    });
  }

  let saveTimer = null;
  const rememberBuyer = () => {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      const record = { v: 1, t: Date.now() };
      let any = false;
      FIELDS.forEach((name) => {
        const input = inputOf(name);
        const value = input && input.value ? input.value.trim() : '';
        if (!value) return;
        record[name] = value;
        any = true;
      });
      if (!any) return;
      /* ⚠ ONLY WHEN IT VALIDATES. A half-typed number normalises to something
         that looks like a phone and matches nobody, and the head snippet cannot
         tell the difference — it trusts `ph` on sight. Same rule as the prefill
         above: checked on the way in, never on the way out. */
      const settled = rules.checkPhone(record.phone || '');
      if (settled.ok) record.ph = settled.value;
      try { localStorage.setItem(BUYER_KEY, JSON.stringify(record)); } catch (e) {}
    }, 500);
  };

  /* -------------------------------------------------------------------------
     THE ATTEMPT ID — one per cart, and the guard against DUPLICATE ORDERS.

     ⚠ The real duplication risk is two Shopify orders, not two Purchase events.
     Event dedup is solved by deriving the id from the order; what that cannot
     fix is two orders, which is two Purchases that are each individually
     correct, two parcels, and a customer phoned about an order they placed
     once.

     Two guards, because they cover different timescales: the submit button is
     disabled for the whole round trip (that is the sub-second double tap, which
     is most of it), and this id lets the Worker recognise a retry, a refresh or
     a flaky-network resubmit and return the ORIGINAL response without touching
     Shopify.

     ⚠ IT IS REGENERATED AFTER A SUCCESSFUL ORDER. Otherwise a customer's second
     genuine order of the day would find the first one's record and be silently
     swallowed — a worse bug than the one this guards against.
     ---------------------------------------------------------------------- */
  const ATTEMPT_KEY = 'rcb-attempt';
  let memoryAttempt = '';

  const newAttemptId = () => {
    let id;
    try {
      id = 'p2_' + crypto.randomUUID();
    } catch (e) {
      id = 'p2_a' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
    }
    memoryAttempt = id;
    try { sessionStorage.setItem(ATTEMPT_KEY, id); } catch (e) {}
    return id;
  };

  const attemptId = () => {
    try {
      const held = sessionStorage.getItem(ATTEMPT_KEY);
      // Keep an existing legacy attempt on its original KV route. Replacing
      // it during rollout could recreate an order whose response was lost.
      if (held) return (memoryAttempt = held);
    } catch (e) {}
    return memoryAttempt || newAttemptId();
  };

  /* -------------------------------------------------------------------------
     Validation, using the generated copy of `order/validate.mjs`.
     ---------------------------------------------------------------------- */
  const showError = (name, message) => {
    const box = cell(name);
    if (!box) return;
    const line = box.querySelector('[data-rcb-error]');
    const input = inputOf(name);
    box.classList.add('is-bad');
    if (input) input.setAttribute('aria-invalid', 'true');
    if (!line) return;
    line.textContent = message;
    line.hidden = false;
  };

  const clearError = (name) => {
    const box = cell(name);
    if (!box) return;
    const line = box.querySelector('[data-rcb-error]');
    const input = inputOf(name);
    box.classList.remove('is-bad');
    if (input) input.removeAttribute('aria-invalid');
    if (!line) return;
    line.hidden = true;
    line.textContent = '';
  };

  const values = () => ({
    name: (inputOf('name') || {}).value || '',
    phone: (inputOf('phone') || {}).value || '',
    address: (inputOf('address') || {}).value || '',
  });

  /* -------------------------------------------------------------------------
     THE FAILED ORDER, CARRIED INTO A CHAT.

     A customer whose order just failed has already typed a name, a number and
     an address, and chosen what they wanted. Handing them a bare chat window
     asks them to do all of it again, in a worse editor, from memory — which is
     why "message us instead" usually recovers nothing. So the tile they tap
     opens with the whole order already written: what is in the bag, the links
     to it, the total, and who they are. One send, and the shop can enter it.

     `wa.me/<number>?text=` is a documented, human-visible prefill. The
     Messenger `m.me/<page>?text=` link carries the opening text through its
     redirect into the conversation. imo publishes no web link scheme, so its
     tile copies the order for the customer to paste into the app.

     ⚠ NOT A WORD OF THE MESSAGE IS WRITTEN HERE. The template is the
     `wa_message` theme setting, arriving as an attribute; this fills its
     placeholders. Even the quantity is `× ২` rather than `২টি`, so that no
     Bangla word ends up authored in a `.js` where nobody would find it.

     ⚠ THE TOTAL IS COPIED, NEVER COMPUTED — it is the string Liquid rendered
     into the button, through the store's own currency format. This theme
     formats no money in JavaScript, and a chat message quoting a total the bill
     disagrees with would be the exact bug the single-total design exists to
     make inexpressible.
     ---------------------------------------------------------------------- */
  const waLink = helpCard && helpCard.querySelector('[data-channel="whatsapp"]');
  const waPlain = waLink ? waLink.getAttribute('href') : '';
  const messengerLink = helpCard && helpCard.querySelector('[data-channel="messenger"]');
  const messengerPlain = messengerLink ? messengerLink.getAttribute('href') : '';

  const BN_DIGITS = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
  /* Prose numbers are Bengali everywhere in this shop; a Bangla screen reader
     announces a Latin `1` in English. Money is the exception and is untouched
     above, because Shopify's currency format sets it. */
  const bengali = (value) => String(value).replace(/[0-9]/g, (d) => BN_DIGITS[Number(d)]);

  const orderMessage = () => {
    const template = root.dataset.rcbWaMessage || '';
    if (!template) return '';

    const totalSlot = form.querySelector('[data-rcb-order-total]');
    const typed = values();
    const filling = {
      items: heldLines.map((line) => {
        const title = (line.product_title || line.title || '').trim();
        if (!title) return '';
        const row = `• ${title} × ${bengali(line.quantity)}`;
        return line.url ? `${row}\n  ${window.location.origin}${line.url}` : row;
      }).filter(Boolean).join('\n'),
      total: totalSlot ? totalSlot.textContent.trim() : '',
      name: typed.name.trim(),
      phone: typed.phone.trim(),
      address: typed.address.trim().replace(/\s*\n\s*/g, ', '),
    };

    /* ⚠ A ROW WHOSE PLACEHOLDER CAME BACK EMPTY IS DROPPED WHOLE, label and
       all. Same rule as the thank-you panel's `fillRow`: `ঠিকানা:` standing
       over nothing reads as a shop that lost the address, and here it would go
       out under the CUSTOMER's name, which is worse. */
    const kept = [];
    template.split('\n').forEach((row) => {
      let empty = false;
      const filled = row.replace(/\[(items|total|name|phone|address)\]/g, (whole, key) => {
        if (!filling[key]) empty = true;
        return filling[key];
      });
      if (!empty) kept.push(filled);
    });
    return kept.join('\n').replace(/\n{3,}/g, '\n\n').trim();
  };

  /* ⚠ PUT BACK ON SUCCESS, and this is not tidying. The contact card stays on
     screen under the thank-you panel, where its job is fixing a wrong digit in
     the number that panel prints — so a tile still loaded with `ওয়েবসাইটে
     অর্ডার করতে পারছি না` would have the customer telling the shop their order
     failed seconds after it succeeded.

     ⚠ THE MESSENGER TILE RECEIVES THE SAME OPENING TEXT as WhatsApp. Its
     `m.me/<page>?text=` link preserves that text through Facebook's redirect
     into the conversation.

     ⚠ THE ইমো TILE COPIES THE ORDER INSTEAD OF THE NUMBER, and it is a real
     trade rather than a free win. imo publishes no link scheme, so copying is
     all a button can do, and it can only hold one thing. The order text is
     worth more than the number here: the number is printed directly above at
     19px and a long press on that `tel:` link offers Copy, while the order is
     the part nobody wants to retype. `data-rcb-copy-number` carries the
     original payload so success restores it exactly. */
  const imoButton = helpCard && helpCard.querySelector('[data-channel="imo"]');

  const loadChannels = (text) => {
    if (waLink && waPlain) {
      waLink.setAttribute('href', text ? `${waPlain}?text=${encodeURIComponent(text)}` : waPlain);
    }
    if (messengerLink && messengerPlain) {
      const separator = messengerPlain.includes('?') ? '&' : '?';
      messengerLink.setAttribute('href', text ? `${messengerPlain}${separator}text=${encodeURIComponent(text)}` : messengerPlain);
    }
    if (imoButton) {
      const number = imoButton.dataset.rcbCopyNumber || '';
      imoButton.dataset.rcbCopy = text || number;
    }
  };

  /* -------------------------------------------------------------------------
     SHOWING A FAILURE — the message, the way out, and the way out loaded.

     ⚠ THE SCROLL IS PART OF THE SENTENCE, not a nicety. Every message that can
     land in this box ends `নিচের নম্বরে মেসেজ দিন` — and on a 390px phone
     `নিচে` was a promise the page did not keep: the contact card sat below the
     button, the note and a hairline, off-screen under the thumb that had just
     tapped. A cash-on-delivery buyer who has been told the order failed does
     not go hunting for a second route. They leave.

     ⚠ THE MESSAGE IS CENTRED, AND CENTRING IT IS WHAT PUTS THE CARD ON SCREEN.
     The failure box is the last thing in the form, one hairline above the
     contact card, with the order button just above it — so parking the box in
     the middle of the viewport leaves the button in the upper half and the
     whole card in the lower half, which is exactly the pair the message names.
     `block: 'nearest'` was tried first and is wrong here: it stops as soon as
     the box is technically visible, which on a tall phone can be one line above
     the fold with the card still entirely below it.

     ⚠ That adjacency is load-bearing: put the box back above the button and
     centring it centres the wrong half of the page.

     ⚠ NOTHING IS DUPLICATED TO ACHIEVE THIS. The obvious alternative — printing
     the number inside the red box — would be a second copy of the shop's phone
     number on a page that already carries `rcb-channels`. One card, one
     printing, brought into view.
     ---------------------------------------------------------------------- */
  const showFail = (message) => {
    if (!failLine) return;
    failLine.textContent = message || '';
    failLine.hidden = false;
    loadChannels(orderMessage());
    failLine.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  /* On blur only, and only once a field has been left — telling somebody their
     phone number is wrong while they are still typing the fourth digit of it is
     how a form feels hostile. On submit every field is checked at once. */
  FIELDS.forEach((name) => {
    const input = inputOf(name);
    if (!input) return;
    input.addEventListener('focus', beginCheckout);
    input.addEventListener('blur', () => {
      if (!input.value.trim()) return;
      const check = name === 'phone' ? rules.checkPhone : name === 'name' ? rules.checkName : rules.checkAddress;
      const result = check(input.value);
      if (result.ok) clearError(name);
      else showError(name, result.message);
    });
    input.addEventListener('input', () => {
      clearError(name);
      if (failLine) failLine.hidden = true;
      rememberBuyer();
      beginCheckout();
    });
  });

  /* -------------------------------------------------------------------------
     `rcb:checkout` — once, on the first real interaction. See the header.
     ---------------------------------------------------------------------- */
  let checkoutFired = false;
  function beginCheckout() {
    if (checkoutFired || !cartRoot || !(Number(cartRoot.dataset.rcbItemCount) > 0)) return;
    // Liquid prices the lines after all allocated discounts; never infer quantity
    // from IDs or fall back to the customer total (which includes delivery).
    let contents = [];
    try {
      const parsed = JSON.parse(cartRoot.dataset.rcbContents || '[]');
      if (Array.isArray(parsed)) contents = parsed;
    } catch (e) { /* Missing analytics data must never block the order. */ }
    checkoutFired = true;
    document.dispatchEvent(new CustomEvent('rcb:checkout', {
      detail: {
        value: Number(cartRoot.dataset.rcbSubtotal) || 0,
        currency: cartRoot.dataset.rcbCurrency || 'BDT',
        contentIds: (cartRoot.dataset.rcbContentIds || '').split(',').filter(Boolean),
        itemCount: Number(cartRoot.dataset.rcbItemCount) || 0,
        contents,
      },
    }));
  }

  /* -------------------------------------------------------------------------
     Attribution the Worker cannot see for itself.

     `_fbp` and `_ttp` identify the browser; they do not describe its source.
     `rcb-visit` captures the current entry before either SDK, retaining it
     across internal hops. Its `visit` object describes the order's source;
     its fresh click ids let conversion matching follow that same visit.
     Never read a historical `_fbc`, `ttclid` or `rcb-utm` here.

     ⚠ `vid` IS THE ONE THAT HAS TO SURVIVE THIS TRIP INTACT. It is the 64-hex
     first-party visitor id from `snippets/rcb-identity.liquid`, and both pixels
     have already sent it as `external_id` on every browser event this visitor
     has raised. Forwarding it here is what lets `order/worker.mjs` and
     `order/confirm.mjs` send the SAME STRING on the server events — which is
     the only reason Meta and TikTok can join a browsing session to the sale at
     the end of it. Before this existed the server's `external_id` was the
     Shopify customer id, a value minted hours later that the browser could
     never have known, so the two halves identified different people.

     ⚠ IT IS PASSED VERBATIM AND MUST NEVER BE HASHED, TRIMMED OR RE-CASED
     ANYWHERE ON THE WAY. 64 lowercase hex is what makes both platforms treat it
     as an already-hashed id and pass it through untouched; change one byte on
     either side and the join is gone with no error anywhere.
     ---------------------------------------------------------------------- */
  const cookie = (name) => {
    const hit = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
    return hit ? decodeURIComponent(hit[1]) : '';
  };

  const attribution = () => {
    const identity = window.RCB_ID || {};
    const out = {
      fbp: cookie('_fbp'),
      ttp: cookie('_ttp'),
      /* Read off the published object rather than the `rcb_vid` cookie, so the
         value posted is exactly the one the pixels sent — the head snippet also
         falls back to a `localStorage` mirror when Safari has expired the
         cookie, and this must follow it there. */
      vid: typeof identity.vid === 'string' ? identity.vid : '',
    };
    try {
      const current = window.RCB_VISIT?.read();
      if (current?.visit?.v === 1) {
        out.visit = current.visit;
        if (current.fbc) out.fbc = current.fbc;
        if (current.ttclid) out.ttclid = current.ttclid;
      }
    } catch (e) {}
    Object.keys(out).forEach((key) => { if (!out[key]) delete out[key]; });
    return out;
  };

  /* -------------------------------------------------------------------------
     THE CART IS READ AT SUBMIT TIME, from `/cart.js`.

     ⚠ Not from Liquid, and this is deliberate. The form is never repainted (a
     repaint would throw away what the customer has typed), so anything Liquid
     wrote into it at page load goes stale the moment a stepper is tapped or a
     suggestion is added — and it also cannot see a change made in another tab.
     One same-origin request at submit costs ~100ms and is the only version of
     this that cannot order the wrong things.
     ---------------------------------------------------------------------- */
  const requestJson = async (url, options, timeoutMs) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      const body = await response.json().catch(() => null);
      return { response, body };
    } finally {
      clearTimeout(timer);
    }
  };
  const readCart = async () => {
    const { response: res, body: cart } = await requestJson('/cart.js',
      { headers: { Accept: 'application/json' } }, 8000);
    if (!res.ok) throw new Error('cart read failed');
    if (!cart) throw new Error('cart response invalid');
    return cart.items || [];
  };


  /* -------------------------------------------------------------------------
     Done: the panel, the cart, the next attempt id.

     ⚠ No pixels. See the header — `Purchase` belongs to the confirmation call
     now, and `rcb-fired-<eventId>` went with it. Nothing on this page needs to
     remember whether an event has been sent, because this page sends none.
     ---------------------------------------------------------------------- */
  const DONE_KEY = 'rcb-last-order';

  /* ⚠ THE PHONE READ-BACK IS GONE — removed 2026-08-23 on request, along with
     `localPhone`, which turned the E.164 `+8801712345678` the Worker is given
     back into the `01712345678` the customer had typed. The panel printed the
     number so a mistyped digit could be caught before the confirmation call
     went out to nobody; `help_line_done` on the contact card below now carries
     that warning without the digits. If the row ever returns, both halves do —
     the conversion is not optional, because a number the customer has to
     mentally re-encode is a number they will not check. */

  const showDone = (order) => {
    if (!donePanel) return;

    /* ⚠ THE ROW IS FILLED OR HIDDEN, never left standing empty beside its own
       label. `showDone` also runs on a refresh, against whatever
       `sessionStorage` holds, and `অর্ডার নম্বর` over a blank reads as a shop
       that has lost the order. */
    const fillRow = (name, value) => {
      const row = donePanel.querySelector(`[data-rcb-done-row="${name}"]`);
      const slot = donePanel.querySelector(`[data-rcb-done-${name}]`);
      const text = value ? String(value).trim() : '';
      if (slot) slot.textContent = text;
      if (row) row.hidden = !text;
      return Boolean(text);
    };

    const hasNumber = fillRow('number', order.orderNumber);
    const ref = donePanel.querySelector('[data-rcb-done-ref]');
    if (ref) ref.hidden = !hasNumber;

    /* ⚠ THE HELP CAPTION IS ADDRESSED TO A DIFFERENT PERSON NOW. It asks
       `অর্ডারে সমস্যা হচ্ছে?` — a question for someone still trying to order,
       still on screen a finger below a panel saying the order is placed. The
       six channels stay exactly as they are; only the words change, to the one
       job they now have: catching a wrong number or address before we call. */
    const helpCaption = document.querySelector('[data-rcb-help-done]');
    if (helpCaption && helpCaption.dataset.rcbHelpDone) {
      helpCaption.textContent = helpCaption.dataset.rcbHelpDone;
    }

    /* ⚠ AND THE WHATSAPP TILE GOES BACK TO A PLAIN CHAT LINK, for the same
       reason and in the same breath. If an earlier attempt failed, that tile is
       still loaded with `ওয়েবসাইটে অর্ডার করতে পারছি না` — which, tapped from
       under a panel confirming the order, has the customer reporting a failure
       that just stopped being true. The caption and the link are the card's two
       halves; both belong to the order now. */
    loadChannels('');

    /* ⚠ THE RECEIPT CARD ITSELF STAYS AND ITS ROWS GO, rather than the card
       being hidden wholesale. The panel is the last child of `.rcbk-doc`, so
       hiding the card would hide the confirmation with it — and keeping the
       card is the better reading anyway: the document the customer was filling
       in becomes the document that confirms it.

       The heading and the back link go too. Both belong to a decision that has
       now been made, and `আরও দেখুন` above a confirmation is an exit offered at
       the one moment nobody is looking for one — the panel's own button is the
       way back into the shop. */
    ['.rcbk-items', '.rcbk-attach', '.rcbk-sum', '.rcbo', '.rcbk-h1', '.rcbk-back'].forEach((selector) => {
      const el = document.querySelector(selector);
      if (el) el.hidden = true;
    });

    donePanel.hidden = false;
    donePanel.setAttribute('tabindex', '-1');
    donePanel.focus({ preventScroll: true });
    donePanel.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  const clearCart = async () => {
    try {
      await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      /* Horizon's header badge lives outside this section and was rendered at
         page load. Speak its protocol rather than reaching into its DOM — the
         same call `rcb-cart.js` makes. */
      const event = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
      event.promise = Promise.resolve({ cart: null, detail: { itemCount: 0 } });
      document.dispatchEvent(event);
    } catch (e) { /* the order exists; a stale cart is cosmetic */ }
  };

  const finish = async (order, fill) => {
    try { sessionStorage.setItem(DONE_KEY, JSON.stringify(order)); } catch (e) {}
    await fill.complete();
    showDone(order);
    clearCart();
    /* ⚠ A NEW ID FOR THE NEXT CART. See the attempt-id note above. */
    newAttemptId();
  };

  /* Bringing the panel back after a refresh, so somebody who reloads the page
     still sees their order number rather than an empty cart. ⚠ This used to be
     the case that made the fire-once guard necessary; with no event to fire it is
     now only about the panel. */
  (() => {
    try {
      const last = JSON.parse(sessionStorage.getItem(DONE_KEY) || 'null');
      if (last && last.orderNumber && cartRoot && Number(cartRoot.dataset.rcbItemCount) === 0) {
        showDone(last);
      }
    } catch (e) {}
  })();

  /* -------------------------------------------------------------------------
     Submit.
     ---------------------------------------------------------------------- */
  // The endpoint has no live percentage. Approach full while waiting, then
  // complete on its accepted response. Never restart a fill on a slow request.
  // ⚠ The curve eases toward its cap rather than reaching it: a linear fill
  // that parks at the cap reads as a hung page on a slow connection, exactly
  // when a customer is deciding whether to back-swipe.
  const FILL_CAP = 0.95;
  const FILL_PACE = 3500;
  const PLACED_HOLD = 650;
  // Time-based, so each line must be true whatever the request is doing.
  const STAGES = [[6000, 'rcbSlowLabel'], [15000, 'rcbStayLabel']];
  const startOrderFill = () => {
    const started = Date.now();
    const reducedMotion = Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
    let active = true;
    let timer;
    const setProgress = (value) => submit.style.setProperty('--rcb-order-progress', String(value));
    const showLabel = (text) => {
      if (!orderLabel || !text || orderLabel.textContent === text) return;
      orderLabel.textContent = text;
      if (!reducedMotion && orderLabel.animate) {
        orderLabel.animate([{ opacity: 0, transform: 'translateY(0.25em)' }, { opacity: 1, transform: 'none' }],
          { duration: 220, easing: 'ease-out' });
      }
    };
    const stage = (elapsed) => {
      const reached = STAGES.filter(([at]) => elapsed >= at).pop();
      if (reached) showLabel(submit.dataset[reached[1]]);
    };
    const tick = () => {
      if (!active) return;
      const elapsed = Math.max(0, Date.now() - started);
      setProgress(0.04 + (FILL_CAP - 0.04) * (1 - Math.exp(-elapsed / FILL_PACE)));
      stage(elapsed);
      timer = setTimeout(tick, 150);
    };
    const stageOnly = () => {
      if (!active) return;
      stage(Math.max(0, Date.now() - started));
      timer = setTimeout(stageOnly, 1000);
    };
    if (reducedMotion) {
      setProgress(0.5);
      timer = setTimeout(stageOnly, 1000);
    } else tick();
    return {
      complete() {
        active = false;
        clearTimeout(timer);
        setProgress(1);
        submit.classList.add('is-placed');
        showLabel(submit.dataset.rcbPlacedLabel);
        // A short beat on the button, so the tap visibly landed before the
        // panel replaces the form.
        return reducedMotion || document.hidden ? Promise.resolve() :
          new Promise((resolve) => setTimeout(resolve, PLACED_HOLD));
      },
      stop() {
        active = false;
        clearTimeout(timer);
        submit.classList.remove('is-placed');
        submit.style.removeProperty('--rcb-order-progress');
      },
    };
  };
  // Leaving mid-request can drop the response to an order Shopify has taken.
  const holdPage = (event) => {
    event.preventDefault();
    event.returnValue = '';
  };
  const releasePage = () => window.removeEventListener('beforeunload', holdPage);
  let inFlight = false;

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (inFlight) return;
    if (cartRoot && cartRoot.dataset.rcbCartBusy === 'true') {
      showFail('ডেলিভারি ও মোট দাম আপডেট হচ্ছে। একটু পরে আবার চেষ্টা করুন।');
      return;
    }

    // Saved details need no input; an invalid submission still starts checkout.
    beginCheckout();

    FIELDS.forEach(clearError);
    if (failLine) failLine.hidden = true;

    const checked = rules.checkOrderForm(values());
    if (!checked.ok) {
      showError(checked.field, checked.message);
      const input = inputOf(checked.field);
      if (input) input.focus();
      return;
    }

    if (!endpoint) {
      showFail(root.dataset.rcbFailLine);
      return;
    }

    /* ⚠ THE FIRST OF THE TWO DUPLICATE-ORDER GUARDS, and the one that closes
       the sub-second double tap that the eventually-consistent KV record
       cannot. It stays disabled for the whole round trip. */
    inFlight = true;
    if (cartRoot) cartRoot.dataset.rcbOrderBusy = 'true';
    submit.disabled = true;
    const idleLabel = orderLabel?.textContent;
    if (orderLabel && submit.dataset.rcbSendingLabel) {
      orderLabel.textContent = submit.dataset.rcbSendingLabel;
    }
    submit.setAttribute('aria-busy', 'true');
    form.classList.add('is-sending');
    const fill = startOrderFill();
    window.addEventListener('beforeunload', holdPage);

    try {
      heldLines = await readCart();
      if (!heldLines.length) {
        releasePage();
        window.location.reload();
        return;
      }
      const items = heldLines.map((line) => ({ id: line.id, quantity: line.quantity }));

      const { response, body: order } = await requestJson(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attemptId: attemptId(),
          items,
          name: checked.value.name,
          phone: checked.value.phone,
          address: checked.value.address,
          website: (form.querySelector('[name="website"]') || {}).value || '',
          /* Compared by the Worker and tagged on a mismatch, never trusted —
             every price on the order comes from the variant itself. */
          deliveryZone: cartRoot?.dataset.rcbDeliveryZone || 'outside',
          clientTotal: Number(cartRoot && cartRoot.dataset.rcbValue) || 0,
          attribution: attribution(),
          pageUrl: window.location.href,
          referrer: document.referrer || '',
        }),
      }, 60000);

      if (!order || !order.ok) {
        if (order && order.field && FIELDS.indexOf(order.field) > -1) {
          showError(order.field, order.message);
          const input = inputOf(order.field);
          if (input) input.focus();
        } else {
          showFail((order && order.message) || 'অর্ডারের উত্তর পাওয়া যায়নি। কয়েক সেকেন্ড পরে আরেকবার অর্ডার বাটনে চাপ দিন। তাতেও না হলে নিচের নম্বরে মেসেজ দিন।');
        }
        return;
      }

      /* ⚠ `order.match` — the customer's own phone and name, carried here so
         the panel could print the number back — is gone with that row. The
         Worker never echoed those details and does not need to: nothing on the
         confirmation panel reads what the customer typed any more. */
      await finish(order, fill);
    } catch (error) {
      /* ⚠ NEVER `form.submit()` AS A FALLBACK. The POST may have landed and
         created the order — a network error is silence, not a refusal — so
         retrying is how one order becomes two. The customer taps again if they
         want to, and the attempt id makes that safe. */
      showFail('অর্ডারের উত্তর পাওয়া যায়নি। কয়েক সেকেন্ড পরে আরেকবার অর্ডার বাটনে চাপ দিন। তাতেও না হলে নিচের নম্বরে মেসেজ দিন।');
    } finally {
      releasePage();
      fill.stop();
      inFlight = false;
      if (cartRoot) delete cartRoot.dataset.rcbOrderBusy;
      submit.disabled = false;
      if (orderLabel) orderLabel.textContent = idleLabel;
      submit.removeAttribute('aria-busy');
      form.classList.remove('is-sending');
    }
  });
})();
